/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useContext, useMemo } from 'react';
import cn from '@helpers/className';
import HotelApi from '@customTypes/hotel-api';
import useIsMobile from '@hooks/useIsMobile';
import useIsBetweenBreakpoints from '@hooks/useIsBetweenBreakpoints';
import RoutingPath from '@constants/routingPath';
import useSessionStorage from '@hooks/useSessionStorage';
import sessionStorageKeys from '@constants/sessionStorageKeys';
// eslint-disable-next-line import/no-named-as-default
import { store } from '@context/store';
import actionTypes from '@context/actionTypes';
import Gallery from '@components/HotelCard/Gallery';
import Amenities from '@components/HotelCard/Amenities';
import FavoritesToastMessage from '@components/MyFavorites/ToastMessage/FavoritesToastMessage';
import { useCookies } from 'react-cookie';
import { unionizeFavorites } from '@helpers/addHotelToFavorites';
import { ShouldShowFairPricingFromHotelAPI } from '@utils/fairPricing';
import Image from 'next/image';
import grayHeartIcon from '@assets/images/gray-heart-icon.svg';
import redHeartIcon from '@assets/images/red-heart-icon.svg';
import updateHotelFavorites from '@helpers/updateHotelFavorites';
import {
  srpAlternateSearchResultClicked,
  srpBadgeHotelCardClicked,
  srpHighlightedSearchResultClicked,
  SRPHotelCardClick,
} from '@events/SRP';
import { hotelFavoriteTappedOnSRP, myFavoritesTappedOnList } from '@events/myFavorites';
import { chain, isArray, isEmpty, isNull, isUndefined } from 'lodash';
import { seoPageCityHotelClicked } from '@events/seoCityPages';
import dateWithinLastNDays from '@helpers/dateWithinLastNDays';
import { trackAlgoliaEvent } from '@utils/services';
import { AlgoliaInsightsPayload } from '@customTypes/algolia';
import { useRouter } from 'next/router';
import { useEvents } from '@events/EventsProvider';
import { SELECT_HOTEL_CARD } from '@constants/amplitudeEvents';
import { getDateWithDashes } from '@helpers/date';
import useAmplitudePayloads from '@hooks/useAmplitudePayloads';
import useFavorites from '@hooks/useFavorites';
import arrowRight from '@assets/images/arrow-right.svg';
import REVIEWS_THRESHOLD_TO_BE_SHOWN from '@constants/yotpo';
import StarRating from '@components/common/Yotpo/StarRating';
import { AMPLITUDE_EXPERIMENT_VARIANT_NAME } from '@constants/amplitudeExperiments';
import Badge from '@components/common/Badge/Badge';
import Products from './Products';
import ProductsWithSTP from './Products/indexWithSTP';

type Props = {
  hotel: HotelApi;
  isSeoPage?: boolean;
  algoliaSearchPosition?: number;
  // eslint-disable-next-line react/require-default-props
  userFavoritesIds?: any;
  // eslint-disable-next-line react/require-default-props
  setUserFavoritesIds?: any;
  selectedVibes?: string[];
};

type Slide = {
  key: string;
  url: string;
  imgClassnames: string;
};

type ImageProps = {
  picture: {
    details: { url: string };
    width_375: { url: string };
    url: string;
  };
};

export default function HotelCard({
  hotel,
  isSeoPage = false,
  algoliaSearchPosition = 1,
  userFavoritesIds,
  setUserFavoritesIds,
  selectedVibes = [],
}: Props) {
  const isMobile = useIsMobile();
  const router = useRouter();
  const minPadding = useIsBetweenBreakpoints(850, 1023);
  const [mobileImages, setMobileImages] = useState<Slide[]>([]);

  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { pricingMarkdownExperiment } = state;

  const [cookies, setCookie] = useCookies(['userInformation']);
  const { getItem, setItem } = useSessionStorage();
  const userLocalStorageFavorites = getItem(sessionStorageKeys.USER_SESSION_FAVORITES);
  const { addHotelToFavorites } = useFavorites();

  const favorites = unionizeFavorites(
    userLocalStorageFavorites,
    cookies.userInformation?.favorites,
  );

  const [userFavorites, setUserFavorites] = useState<any>(
    userFavoritesIds && userFavoritesIds.length > 0 ? userFavoritesIds : favorites,
  );
  const [showFavoriteToastMessage, setShowFavoriteToastMessage] = useState<boolean>(false);
  const [isUserViewingFavoritesInMyAccount, setIsUserViewingFavoritesInMyAccount] =
    useState<boolean>(false);
  const [saveToFavorites, setSaveToFavorites] = useState<boolean>(false);
  const [showHideHotelProducts, setShowHideHotelProducts] = useState<boolean>(false);

  const { generateFilterPayload, generateHotelPayload, generateSearchLocationPayload } =
    useAmplitudePayloads();
  const { track } = useEvents();
  const params = router.query;
  const sessionID = getItem(sessionStorageKeys.SESSION_ID);

  const showFairPricing = ShouldShowFairPricingFromHotelAPI(hotel);

  // -------------------------------------------------------------------
  // VIBES BADGE -------------------------------------------------------
  // -------------------------------------------------------------------
  const vibesBadge = useMemo(() => {
    if (!hotel.vibes) return '';
    if (!(hotel.vibes.primary || hotel.vibes.secondary)) return '';
    if (!selectedVibes || !selectedVibes.length) {
      return hotel.vibes.primary || hotel.vibes.secondary;
    }
    if (
      hotel.vibes.primary &&
      (selectedVibes.includes(hotel.vibes.primary) ||
        selectedVibes.includes(hotel.vibes.primary.toLocaleLowerCase()))
    ) {
      return hotel.vibes.primary;
    }
    if (
      hotel.vibes.secondary &&
      (selectedVibes.includes(hotel.vibes.secondary) ||
        selectedVibes.includes(hotel.vibes.secondary.toLocaleLowerCase()))
    ) {
      return hotel.vibes.secondary;
    }
    return '';
  }, [hotel, selectedVibes]);

  const highlightedStyles =
    'bg-rp-yellow-highlighted border-rp-yellow-border d:rounded-20 mx-0 pt-6 pb-3 d:border d:px-0 d:py-0';

  const isSRP = router.pathname.startsWith(RoutingPath.SRP);

  useEffect(() => {
    const images = hotel.image.map((image: ImageProps) => ({
      key: image.picture.details.url,
      url: image.picture.details.url,
      imgClassnames: 'w-full swiper-lazy rounded-xl',
      urlShort: image.picture.url,
    }));
    if (hotel.imageDesktop !== images[0]?.urlShort) {
      const filteredImages = images.filter((item) => item.urlShort !== hotel.imageDesktop);
      filteredImages.unshift({
        key: hotel.imageDesktop,
        url: hotel.imageDesktop,
        imgClassnames: 'w-full swiper-lazy rounded-xl',
        urlShort: hotel.imageDesktop,
      });
      setMobileImages(filteredImages);
      return;
    }
    setMobileImages(images);
  }, [hotel.image, hotel.imageDesktop, hotel.name]);

  const generateDLPUrl = () => {
    const rootUrl = `${RoutingPath.DLP}/${hotel.url}`;

    // Pick only the URL parameters that start with "utm_" and construct the query string
    const queryString = chain(params)
      .pickBy((value, key) => key.startsWith('utm_') && !isUndefined(value) && !isNull(value))
      .map((value, key) => {
        // Handle arrays properly
        if (isArray(value)) {
          return value.map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join('&');
        }

        return `${encodeURIComponent(key)}=${encodeURIComponent(value || '')}`;
      })
      .join('&')
      .value();

    return `${rootUrl}${queryString ? `?${queryString}` : ''}`;
  };

  const navigateToDetailsPage = () => {
    const { isHighlighted } = hotel;

    if (window) {
      dispatch({
        type: actionTypes.UPDATE_SRP_SCROLL_POSITION,
        payload: window?.scrollY,
      });
    }

    // Algolia Insights
    const { searchResults } = state;
    // Don't use algolia in the favorites page, since it's not in a search context
    if (searchResults && !window.location.href.includes('my-favorites')) {
      const user = cookies.userInformation;

      if (!isEmpty(searchResults)) {
        const result = isArray(searchResults) ? searchResults[0] : searchResults;

        const algoliaSearch: AlgoliaInsightsPayload = {
          event_type: 'click',
          index_name: result.indexName as string,
          object_id: hotel.objectID,
          position: (+algoliaSearchPosition! + 1).toString(),
          query_id: result.queryID,
          user_token: user ? user.id : sessionID,
        };

        trackAlgoliaEvent(algoliaSearch);

        // Set the Algolia Hotel's data to be used in the conversion event.
        setItem(
          sessionStorageKeys.ALGOLIA_TRACKED_HOTEL,
          JSON.stringify({ ...algoliaSearch, hotel_id: hotel.id }),
        );
      }
    }

    if (window.location.href.indexOf('highlighted') > -1) {
      if (isHighlighted) {
        srpHighlightedSearchResultClicked();
      } else {
        srpAlternateSearchResultClicked();
      }
    }
    // Event
    SRPHotelCardClick(hotel.name);

    // Trigger Amplitude event only for SRP pages
    // *cities is not included since is out of search context (filter & search data are needed).
    if (
      router.pathname.startsWith(RoutingPath.SRP) ||
      router.pathname.startsWith(RoutingPath.RESULTS)
    ) {
      const hotelPropertyGroup = generateHotelPayload(hotel, undefined);
      const filterPropertyGroup = generateFilterPayload(
        state.searchFilters,
        state.availableSearchFilterOptions,
        state.onlyShowAvailableHotels,
      );
      const searchLocationProperty = generateSearchLocationPayload(state.searchedLocation || null);

      track(SELECT_HOTEL_CARD, {
        highlighted: !!isHighlighted,
        index: algoliaSearchPosition ? +algoliaSearchPosition + 1 : 1,
        search_date: state && state.searchDate ? getDateWithDashes(state.searchDate) : null,
        ...filterPropertyGroup,
        ...hotelPropertyGroup,
        ...searchLocationProperty,
      });
    }

    if (window.location.href.includes('my-favorites')) {
      // Event
      myFavoritesTappedOnList(hotel.id, hotel.name);
    }

    if (window.location.href.includes('cities') || isSeoPage) {
      // Event
      seoPageCityHotelClicked(hotel.id, hotel.name);
    }

    // RecentlyAddedBadgeEvent
    if (dateWithinLastNDays(hotel.createdAt, 90)) {
      srpBadgeHotelCardClicked();
    }

    // Open DLP in a new tab (it includes utm params)
    window.open(generateDLPUrl(), '_blank');
  };

  const handleAddingHotelIdToFavorites = () => {
    addHotelToFavorites(
      cookies,
      setCookie,
      hotel,
      setShowFavoriteToastMessage,
      userFavorites,
      setSaveToFavorites,
      setItem,
      getItem,
      setUserFavorites,
    );

    if (!userFavorites.includes(hotel.id.toString())) {
      hotelFavoriteTappedOnSRP(hotel.id, hotel.name);
    }
  };

  useEffect(() => {
    if (
      window.location.href.includes('my-favorites') ||
      window.location.href.includes('bookings')
    ) {
      setShowHideHotelProducts(true);
    }
    if (window.location.href.includes('bookings')) {
      setIsUserViewingFavoritesInMyAccount(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (setUserFavoritesIds) {
      setUserFavoritesIds(userFavorites);
    }
  }, [setUserFavoritesIds, saveToFavorites, userFavorites]);

  useEffect(() => {
    if (
      userFavorites &&
      !(window.location.href.includes('my-favorites') || window.location.href.includes('bookings'))
    ) {
      updateHotelFavorites(cookies, null, null, getItem, setUserFavorites, null, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {showFavoriteToastMessage && <FavoritesToastMessage saveToFavorites={saveToFavorites} />}
      <div
        className={`relative flex flex-column ${
          // eslint-disable-next-line no-nested-ternary
          hotel.hotSpot && isSRP ? (!hotel.isHighlighted ? 'mt-8' : 'd:mt-8') : ''
        }`}
      >
        {hotel.hotSpot && isSRP ? (
          <div
            className="absolute bg-solar-surface-brand-ultramarine h-full max-w-1024 justify-center rounded-xl z-10 -top-6 d:-top-8 hidden d:flex"
            style={{ width: 'calc(100% - 3rem)' }}
          >
            <span className="font-rp-pn-light mt-6px d:mt-9.5px text-white font-semibold text-13 d:text-base d:leading-14px leading-14px">
              Hot Spot Savings
            </span>
          </div>
        ) : null}
        <a
          role="button"
          tabIndex={0}
          onClick={(e: any) => {
            e.preventDefault();
            if (!e.target?.alt && e.target?.alt !== 'heart') {
              navigateToDetailsPage();
            }
          }}
          onKeyPress={(e: any) => {
            e.preventDefault();
            if (!e.target?.alt && e.target?.alt !== 'heart') {
              navigateToDetailsPage();
            }
          }}
          className={cn({
            'max-w-1024 mx-0 mt-0 flex flex-col bg-white outline-none text-rp-primary-black d:cursor-pointer d:shadow-card d:rounded-xl d:flex-row w-full z-20':
              true,
            'pt-12': !!(hotel.hotSpot && isSRP && hotel.isHighlighted),
            [highlightedStyles]: !!hotel.isHighlighted,
          })}
        >
          <Gallery
            classes={`px-6 ${minPadding ? 'd:w-4/12 cursor-pointer' : 'd:w-5/12 cursor-pointer'}`}
            name={hotel.name}
            mainImage={hotel.imageDesktop}
            mobileImages={mobileImages}
            compact={isMobile}
            navigateToDetailsPage={() => {
              if (!isMobile) {
                navigateToDetailsPage();
              }
            }}
            userFavorites={userFavorites}
            handleAddingHotelIdToFavorites={handleAddingHotelIdToFavorites}
            hotel={hotel}
            hotelUrl={`/hotels/${hotel.url}`}
            isUserViewingFavoritesInMyAccount={isUserViewingFavoritesInMyAccount}
            isSRP={isSRP}
          />

          <div
            className={`flex-grow w-full relative d:${
              minPadding ? 'px-6' : 'px-8'
            } pt-0 d:pt-10 d:pb-6 ${minPadding ? 'd:w-8/12' : 'd:w-7/12'}`}
          >
            <div
              className="hidden mb-2 d:block text-11 font-rp-pn-semi-bold uppercase text-custom-gray-2 tracking-0.6"
              data-testid="hotel-city-and-state"
            >
              {hotel.code ? `${hotel.cityName}, ${hotel.code}` : `${hotel.cityName}`}
            </div>

            <div
              className={`hidden absolute top-11 cursor-pointer ${
                minPadding ? 'right-6' : 'right-8'
              } d:block text-11 font-rp-pn-semi-bold uppercase text-custom-gray-2 tracking-0.6 d:w-1/4 d:text-right`}
            >
              <Image
                src={
                  userFavorites && userFavorites.includes(hotel.id.toString())
                    ? redHeartIcon
                    : grayHeartIcon
                }
                alt="heart"
                onClick={handleAddingHotelIdToFavorites}
              />
            </div>

            <h2
              className="mb-0 w-full text-19 leading-7 font-rp-pn-semi-bold px-6 d:px-0 d:text-22 d:w-3/4 d:-mt-1 d:mb-10px"
              id={hotel.name}
            >
              {hotel.name}
            </h2>

            <div className="flex items-center mt-2 px-6 d:mt-0 d:px-0 d:mb-2.5 mb-2 space-x-2">
              {vibesBadge && (
                <Badge tagClass="bg-rp-primary-new text-white" tagContent={vibesBadge} upperCase />
              )}

              {hotel.rating && hotel.rating > 0 ? (
                <StarRating
                  algoliaRating={hotel.rating}
                  algoliaReviews={hotel.reviewsCount}
                  reviewsThreshold={REVIEWS_THRESHOLD_TO_BE_SHOWN}
                  isSRP
                />
              ) : null}
            </div>

            <div className="overflow-hidden pl-6 d:pl-0 d:overflow-visible relative">
              <Amenities amenities={hotel.amenities} />
            </div>

            {isUserViewingFavoritesInMyAccount ? (
              ''
            ) : (
              <div
                className="hidden mt-10px text-custom-gray-2 text-sm font-rp-pn-regular d:block"
                data-testid="hotel-short-description"
              >
                {hotel.shortDescription}
              </div>
            )}

            <div
              className="mt-0 flex flex-col relative d:flex-row d:mt-8 d:items-center overflow-auto"
              data-testid="hotel-card-products"
            >
              {hotel.products && hotel.products.length > 0 && !showHideHotelProducts ? (
                pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ? (
                  <ProductsWithSTP
                    minPadding={minPadding}
                    isSeoPage={isSeoPage}
                    hotel={hotel}
                    showFairPricing={showFairPricing}
                  />
                ) : (
                  <Products
                    minPadding={minPadding}
                    isSeoPage={isSeoPage}
                    hotel={hotel}
                    showFairPricing={showFairPricing}
                  />
                )
              ) : (
                <div className={`${isUserViewingFavoritesInMyAccount ? '' : 'd:h-24 h-0'}`} />
              )}

              {isSRP && !isMobile ? (
                <button
                  data-testid={`hotel-${hotel.id}-card-view-button`}
                  type="button"
                  onClick={navigateToDetailsPage}
                  className="absolute bg-white border border-rp-gray-divider bottom-5 h-9 right-0 rounded-full w-9 flex items-center justify-center"
                >
                  <Image src={arrowRight} alt={`View ${hotel.name}`} width={18} height={18} />
                </button>
              ) : null}
            </div>
          </div>
        </a>
      </div>
    </>
  );
}
